import type { Edge, Node } from '@xyflow/react';
import {
  isAttackPathOverlayRole,
  type AttackPathEventEdgeData,
  type AttackPathEventNodeData,
} from './attack-path-event-node.types';
import {
  isSeedRole,
  resolveDestinationPull,
  resolveSpotlightDim,
  type DestinationPullLevel,
} from './attack-path-reveal-theater.util';

type RevealState = {
  contentHiddenById: ReadonlyMap<string, boolean>;
  pathInkEdgeIds: ReadonlySet<string>;
  revealActiveCampaignIds: ReadonlySet<string> | null;
  revealUpToColumn?: number;
  isRevealing: boolean;
  cinematic?: boolean;
};

type FocusState = {
  focusActive: boolean;
  focusBrightNodeIds?: ReadonlySet<string> | null;
};

const collectPathInkNodeIds = ({
  edges,
  pathInkEdgeIds,
}: {
  edges: ReadonlyArray<Edge<AttackPathEventEdgeData>>;
  pathInkEdgeIds: ReadonlySet<string>;
}): Set<string> => {
  const nodeIds = new Set<string>();

  for (const edge of edges) {
    if (!pathInkEdgeIds.has(edge.id)) {
      continue;
    }

    nodeIds.add(edge.source);
    nodeIds.add(edge.target);
  }

  return nodeIds;
};

const collectRevealLinkedSeedIds = ({
  edges,
  revealActiveCampaignIds,
}: {
  edges: ReadonlyArray<Edge<AttackPathEventEdgeData>>;
  revealActiveCampaignIds: ReadonlySet<string> | null;
}): Set<string> => {
  const nodeIds = new Set<string>();

  if (!revealActiveCampaignIds) {
    return nodeIds;
  }

  for (const edge of edges) {
    if (
      edge.data?.campaignId == null ||
      !revealActiveCampaignIds.has(edge.data.campaignId)
    ) {
      continue;
    }

    nodeIds.add(edge.source);
    nodeIds.add(edge.target);
  }

  return nodeIds;
};

const isRevealFrontierNode = ({
  node,
  revealUpToColumn,
}: {
  node: Node<AttackPathEventNodeData> | undefined;
  revealUpToColumn?: number;
}): boolean =>
  node !== undefined &&
  revealUpToColumn !== undefined &&
  node.data.column === revealUpToColumn &&
  !isSeedRole(node.data.role);

export const decorateAttackPathFlowNodes = ({
  sourceNodes,
  sourceEdges,
  frameHiddenById,
  climaxNodeIds,
  maxContentColumn,
  selectedNodeId,
  contentHiddenById,
  pathInkEdgeIds,
  revealActiveCampaignIds,
  revealUpToColumn,
  isRevealing,
  cinematic,
  focusActive,
  focusBrightNodeIds,
}: RevealState &
  FocusState & {
    sourceNodes: ReadonlyArray<Node<AttackPathEventNodeData>>;
    sourceEdges: ReadonlyArray<Edge<AttackPathEventEdgeData>>;
    frameHiddenById: ReadonlyMap<string, boolean>;
    climaxNodeIds: ReadonlySet<string>;
    maxContentColumn: number;
    selectedNodeId?: string | null;
  }): Node<AttackPathEventNodeData>[] => {
  const nodeById = new Map(sourceNodes.map((node) => [node.id, node]));
  const pathInkNodeIds = collectPathInkNodeIds({
    edges: sourceEdges,
    pathInkEdgeIds,
  });
  const linkedSeedIds = collectRevealLinkedSeedIds({
    edges: sourceEdges,
    revealActiveCampaignIds,
  });
  const jewelLinked = sourceEdges.some((edge) => {
    const target = nodeById.get(edge.target);

    return (
      (target?.data.role === 'CROWN_JEWEL_SEED' ||
        target?.data.role === 'CROWN_JEWEL_CLUSTER') &&
      !(contentHiddenById.get(edge.source) ?? true) &&
      !(contentHiddenById.get(edge.target) ?? true)
    );
  });
  const destinationPull: DestinationPullLevel = cinematic
    ? resolveDestinationPull({
        revealUpToColumn,
        maxContentColumn,
        hasVisibleLink: jewelLinked,
      })
    : 'idle';

  return sourceNodes.map((node) => {
    const contentHidden = isAttackPathOverlayRole(node.data.role)
      ? (frameHiddenById.get(node.id) ?? false)
      : (contentHiddenById.get(node.id) ?? false);
    const revealCampaignActive =
      node.data.campaignId != null &&
      (revealActiveCampaignIds?.has(node.data.campaignId) ?? false);
    const seedLinked =
      isSeedRole(node.data.role) &&
      revealActiveCampaignIds != null &&
      linkedSeedIds.has(node.id);
    const highlighted =
      revealCampaignActive ||
      seedLinked ||
      (focusActive && (focusBrightNodeIds?.has(node.id) ?? false));
    const spotlightActive =
      Boolean(cinematic) &&
      isRevealing &&
      !contentHidden &&
      !isAttackPathOverlayRole(node.data.role) &&
      !isSeedRole(node.data.role) &&
      node.data.column === revealUpToColumn;
    const pathInkActive = !contentHidden && pathInkNodeIds.has(node.id);
    const nodeDestinationPull =
      node.data.role === 'CROWN_JEWEL_SEED' ||
      node.data.role === 'CROWN_JEWEL_CLUSTER'
        ? destinationPull
        : 'idle';
    const climaxActive = Boolean(cinematic) && climaxNodeIds.has(node.id);
    const spotlightDim = resolveSpotlightDim({
      isRevealing,
      hidden: contentHidden,
      column: node.data.column,
      revealUpToColumn,
      role: node.data.role,
      pathInkActive,
      climaxActive,
      destinationPull: nodeDestinationPull,
      spotlightActive,
    });
    const revealFrameDim =
      isRevealing &&
      node.data.role === 'CAMPAIGN_FRAME' &&
      !contentHidden &&
      !highlighted;
    const focusDim =
      focusActive && !(focusBrightNodeIds?.has(node.id) ?? false);

    return {
      ...node,
      zIndex: isAttackPathOverlayRole(node.data.role) ? -1 : 1,
      selected: node.id === selectedNodeId,
      data: {
        ...node.data,
        hidden: contentHidden,
        highlighted,
        climaxActive,
        entranceActive: false,
        spotlightActive,
        pathInkActive,
        bootActive: false,
        destinationPull: nodeDestinationPull,
        dimmed:
          (Boolean(node.data.dimmed) &&
            node.id !== selectedNodeId &&
            !highlighted) ||
          spotlightDim ||
          revealFrameDim ||
          focusDim ||
          (selectedNodeId != null &&
            selectedNodeId !== node.id &&
            !highlighted),
      },
    };
  });
};

export const decorateAttackPathFlowEdges = ({
  sourceNodes,
  sourceEdges,
  drawingEdgeIds,
  contentHiddenById,
  pathInkEdgeIds,
  revealActiveCampaignIds,
  revealUpToColumn,
  isRevealing,
  focusActive,
  focusBrightNodeIds,
}: RevealState &
  FocusState & {
    sourceNodes: ReadonlyArray<Node<AttackPathEventNodeData>>;
    sourceEdges: ReadonlyArray<Edge<AttackPathEventEdgeData>>;
    drawingEdgeIds: ReadonlySet<string>;
  }): Edge<AttackPathEventEdgeData>[] => {
  const nodeById = new Map(sourceNodes.map((node) => [node.id, node]));

  return sourceEdges.map((edge) => {
    const hidden =
      (contentHiddenById.get(edge.source) ?? true) ||
      (contentHiddenById.get(edge.target) ?? true);
    const edgeCampaignId = edge.data?.campaignId ?? null;
    const revealCampaignActive =
      edgeCampaignId != null &&
      (revealActiveCampaignIds?.has(edgeCampaignId) ?? false);
    const campaignDimmed =
      isRevealing && edgeCampaignId != null && !revealCampaignActive;
    const pathInkActive = pathInkEdgeIds.has(edge.id);
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    const spotlightActive =
      isRevealing &&
      !hidden &&
      (isRevealFrontierNode({ node: sourceNode, revealUpToColumn }) ||
        isRevealFrontierNode({ node: targetNode, revealUpToColumn }));
    const focusEdge =
      focusActive &&
      (focusBrightNodeIds?.has(edge.source) ?? false) &&
      (focusBrightNodeIds?.has(edge.target) ?? false);

    return {
      ...edge,
      data: {
        ...(edge.data ?? {
          label: null,
          variant: 'proven' as const,
          campaignId: null,
        }),
        hidden,
        dimmed:
          hidden ||
          (isRevealing && campaignDimmed && !pathInkActive) ||
          (focusActive && !focusEdge),
        highlighted: Boolean(pathInkActive || spotlightActive || focusEdge),
        impactPath: focusEdge,
        energyActive: !hidden && !isRevealing,
        drawActive: drawingEdgeIds.has(edge.id),
        pathInkActive,
        spotlightActive,
      },
    };
  });
};
